import {naturalSort} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {
  ProcessSchemaMapperType,
  ProcessMapperTokenType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  findAllUpstreamMappableNodeIds,
  isMappableTargetNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  TransitionSource,
  FlowchartStaticEdgeData,
  TaskSchemaNodeSectionType,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaDomainData,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource} from './TransitionSourceUtils';
import {
  getCustomMappingDataForDataFlowEdge,
  getTransitionSourceForNodeIdPair,
} from './types/transition/transitionAdapter';
import {parseFieldPairKey} from './types/transition/transitionKeyUtils';
import {Transition} from './types/transition/TransitionTypes';

interface BuildSchemaMapperConfigsFromStaticDataRunnerArgs {
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
  transitions: Record<string, Transition>;
  // Optional root node static data, which is generally only included here if
  // the root node is not part of the normal static nodes data (e.g. for
  // validation flowcharts we want to include the root node static data without
  // including the root node in the normal nodes array)
  rootNodeStaticData?: TaskSchemaDomainData;
}

export interface BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
  inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
}

class BuildSchemaMapperConfigsFromStaticDataRunner {
  private readonly nodes: Array<StaticFlowchartNodeData>;
  private readonly edges: Array<FlowchartStaticEdgeData>;
  private readonly transitions: Record<string, Transition>;

  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private rootNode: TaskSchemaStaticNodeData | undefined;
  // Mapping of a hashkey based on PTDVs to schema mapper configs for deduplication
  private existingSchemaMapperConfigs: Map<string, ProcessFlowchartSchemaMapperConfigSpec>;

  constructor(args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs) {
    this.nodes = args.nodes;
    this.edges = args.edges;
    this.transitions = args.transitions;

    this.inboundSchemaMapperConfigsByNodeId = {};
    this.rootNode =
      this.nodes.find(
        (node): node is TaskSchemaStaticNodeData =>
          isStaticTaskSchemaNodeData(node) &&
          node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) ?? args.rootNodeStaticData;
    this.existingSchemaMapperConfigs = new Map();
  }

  /**
   * Creates a unique key for schema mapper config deduplication based on task definition versions.
   * For merging transitions, the key includes all sorted source PTDV IDs to uniquely identify
   * the transition.
   */
  private getSchemaMapperConfigKey(
    fromTaskDefinitionVersionIds: Array<string>,
    toTaskDefinitionVersionId: string,
    mapsToType: ProcessMapperTokenType
  ): string {
    const sortedSources = naturalSort([...fromTaskDefinitionVersionIds]);
    return `${sortedSources.join('+')}-${toTaskDefinitionVersionId}-${mapsToType}`;
  }

  private getSchemaMapperClientId(
    sourcePTDVIds: Array<string>,
    targetNodeId: string,
    mapsToType: ProcessMapperTokenType
  ): string {
    const sortedSourceIds = naturalSort([...sourcePTDVIds]);
    return `schema-mapper-${sortedSourceIds.join('+')}-${targetNodeId}-${mapsToType}`;
  }

  private getMapsToType(
    nodeDetailsType: ProcessFlowchartNodeDetailsType | undefined
  ): ProcessMapperTokenType {
    return nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;
  }

  private createAndStoreSchemaMapperConfig(params: {
    sourcePTDVIds: Array<string>;
    targetPTDVId: string;
    targetNodeId: string;
    mapsToType: ProcessMapperTokenType;
    schemaFieldMapperConfigs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec>;
    schemaMapperSourceConfigs: Array<{
      mapsFromType: ProcessMapperTokenType;
      fromProcessTaskDefinitionVersionClientId: string;
    }>;
  }): ProcessFlowchartSchemaMapperConfigSpec {
    const clientId = this.getSchemaMapperClientId(
      params.sourcePTDVIds,
      params.targetNodeId,
      params.mapsToType
    );
    const config: ProcessFlowchartSchemaMapperConfigSpec = {
      clientId,
      toProcessTaskDefinitionVersionClientId: params.targetPTDVId,
      mapsToType: params.mapsToType,
      schemaFieldMapperConfigs: params.schemaFieldMapperConfigs,
      mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
      schemaMapperSourceConfigs: params.schemaMapperSourceConfigs,
    };
    const schemaMapperKey = this.getSchemaMapperConfigKey(
      params.sourcePTDVIds,
      params.targetPTDVId,
      params.mapsToType
    );
    this.existingSchemaMapperConfigs.set(schemaMapperKey, config);
    return config;
  }

  run(): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
    const schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec> = [];
    // Build schema mapper configs for transitions
    for (const transition of Object.values(this.transitions)) {
      const spec = this.buildSchemaMapperConfigForTransition(transition);
      if (spec) {
        schemaMapperConfigSpecs.push(spec);
      }
    }

    // Add inbound schema mapper configs for each target node
    for (const node of this.nodes) {
      if (isMappableTargetNode({id: node.id, data: node})) {
        assert(isStaticTaskSchemaNodeData(node), 'Only task schema type nodes are expected to be mapped to');
        this.populateInboundSchemaMapperConfigsForTargetNode(node);
      }
    }

    return {
      schemaMapperConfigSpecs,
      inboundSchemaMapperConfigsByNodeId: this.inboundSchemaMapperConfigsByNodeId,
    };
  }

  /**
   * Adds inbound schema mapper configs for a target node
   */
  private populateInboundSchemaMapperConfigsForTargetNode(targetNode: TaskSchemaStaticNodeData): void {
    const upstreamMappableNodeIds = new Set(
      findAllUpstreamMappableNodeIds(
        {nodes: this.nodes.map((node) => ({id: node.id, data: node})), edges: this.edges},
        targetNode.id
      )
    );
    // Add any additional upstream nodes that have data flow edges to the target node, since those
    // also need to be included in the schema mapper config dependencies and may be the source of
    // field mappings for the target node
    for (const edge of this.edges) {
      if (edge.target === targetNode.id && edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        upstreamMappableNodeIds.add(edge.source);
      }
    }

    if (this.inboundSchemaMapperConfigsByNodeId[targetNode.id] == null) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id] = [];
    }

    const upstreamMappableNodes = this.nodes.filter(
      (node): node is TaskSchemaStaticNodeData =>
        upstreamMappableNodeIds.has(node.id) && isStaticTaskSchemaNodeData(node)
    );

    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    // Group source nodes by their transition to the target.
    // This handles merging transitions (multiple sources to same target node).
    const sourceNodeGroups = this.groupSourceNodesByTransition(upstreamMappableNodes, targetPTDVId);

    sourceNodeGroups.forEach(({sourceNodes, transition}) => {
      this.populateInboundSchemaMapperConfigs(sourceNodes, targetNode, transition);
    });
  }

  /**
   * Groups upstream source nodes by their transition to the target.
   * Nodes that share a merging transition (multiple sources to same target) are grouped together.
   * When multiple nodes share the same PTDV within a transition, they are split into separate
   * groups so we produce multiple inbound schema mapper configs
   *
   * Example: If nodes A1 and A2 both use PTDV "X" and both transition to B, they are split into
   * [A1] and [A2] so each gets its own schema mapper config referencing the same PTDV.
   */
  private groupSourceNodesByTransition(
    sourceNodes: Array<TaskSchemaStaticNodeData>,
    targetPTDVId: string
  ): Array<{sourceNodes: Array<TaskSchemaStaticNodeData>; transition: Transition}> {
    const upstreamPTDVIds = new Set(sourceNodes.map((n) => n.taskDefinitionVersionSpec.clientId));
    const groups: Array<{
      sourceNodes: Array<TaskSchemaStaticNodeData>;
      transition: Transition;
    }> = [];
    const seenSourceNodes = new Set<string>();

    for (const sourceNode of sourceNodes) {
      if (seenSourceNodes.has(sourceNode.id)) {
        continue;
      }

      const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
      const matchingTransition = this.findTransitionForSourceTarget(
        sourcePTDVId,
        targetPTDVId,
        upstreamPTDVIds
      );

      if (matchingTransition == null) {
        continue;
      }

      const involvedSourceNodes = matchingTransition.sourcePTDVIds.map((ptdvId) => {
        const possibleSourceNodes = sourceNodes.filter(
          (node) => node.taskDefinitionVersionSpec.clientId === ptdvId
        );
        return possibleSourceNodes.find((node) => !seenSourceNodes.has(node.id)) || possibleSourceNodes[0];
      });

      groups.push({
        transition: matchingTransition,
        sourceNodes: involvedSourceNodes,
      });
      involvedSourceNodes.forEach((node) => seenSourceNodes.add(node.id));
    }

    return groups;
  }

  /**
   * Builds a schema mapper config for a transition.
   * The resulting spec goes into the top-level schemaMapperConfigs
   */
  private buildSchemaMapperConfigForTransition(
    transition: Transition
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    const mapsToType =
      transition.targetPTDVId === this.rootNode?.taskDefinitionVersionSpec.clientId
        ? ProcessMapperTokenType.PROCESS_OUTPUT
        : ProcessMapperTokenType.PROCESS_TASK;

    const schemaMapperKey = this.getSchemaMapperConfigKey(
      transition.sourcePTDVIds,
      transition.targetPTDVId,
      mapsToType
    );

    if (this.existingSchemaMapperConfigs.has(schemaMapperKey)) {
      return null;
    }

    const schemaMapperSourceConfigs = transition.sourcePTDVIds.map((sourcePTDVId) => {
      const transitionSource =
        transition.transitionSourcesBySourcePTDVId[sourcePTDVId] ??
        this.getDefaultTransitionSourceForPTDVId(sourcePTDVId);
      const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);
      return {
        mapsFromType,
        fromProcessTaskDefinitionVersionClientId: sourcePTDVId,
      };
    });

    return this.createAndStoreSchemaMapperConfig({
      sourcePTDVIds: transition.sourcePTDVIds,
      targetPTDVId: transition.targetPTDVId,
      targetNodeId: transition.targetPTDVId,
      mapsToType,
      schemaFieldMapperConfigs: this.buildSchemaFieldMapperConfigsForTransition(transition),
      schemaMapperSourceConfigs,
    });
  }

  private buildSchemaFieldMapperConfigsForTransition(
    transition: Transition
  ): Array<ProcessFlowchartSchemaFieldMapperConfigSpec> {
    const configs: Array<ProcessFlowchartSchemaFieldMapperConfigSpec> = [];

    for (const [fieldPairKey, customMappingData] of Object.entries(transition.customMappingDataByFieldPair)) {
      const parsed = parseFieldPairKey(fieldPairKey);
      if (parsed == null) {
        continue;
      }

      const transitionSource =
        transition.transitionSourcesBySourcePTDVId[parsed.sourcePTDVId] ??
        this.getDefaultTransitionSourceForPTDVId(parsed.sourcePTDVId);
      const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

      const lookupConfig =
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.buildDefaultLookupConfig(
              parsed.sourceSectionType,
              transitionSource,
              parsed.sourceFieldClientId,
              parsed.sourcePTDVId
            );

      configs.push({
        fromProcessTaskDefinitionVersionClientId: parsed.sourcePTDVId,
        mapsFromType,
        toProcessFieldClientIdentifier: parsed.targetFieldClientId,
        resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
        lookupConfig,
      });
    }

    return configs;
  }

  /**
   * Finds a transition that includes the given source PTDV, has the given target PTDV, and
   * whose full source set is present in `upstreamPTDVIds` (i.e., fully realized at this node).
   * Validation should ensure that there is exactly one transition that fulfills this
   */
  private findTransitionForSourceTarget(
    sourcePTDVId: string,
    targetPTDVId: string,
    upstreamPTDVIds: Set<string>
  ): Transition | null {
    for (const transition of Object.values(this.transitions)) {
      if (transition.targetPTDVId !== targetPTDVId || !transition.sourcePTDVIds.includes(sourcePTDVId)) {
        continue;
      }
      if (transition.sourcePTDVIds.every((id) => upstreamPTDVIds.has(id))) {
        return transition;
      }
    }
    return null;
  }

  /**
   * Builds a schema mapper config for a transition (supporting multiple sources for merging transitions).
   * @param sourceNodes - Array of source nodes
   * @param targetNode - The target node
   * @param matchingTransition - The transition for these source nodes and target nodes
   * @returns A schema mapper config spec, or null if one already exists for this transition
   */
  private populateInboundSchemaMapperConfigs(
    sourceNodes: Array<TaskSchemaStaticNodeData>,
    targetNode: TaskSchemaStaticNodeData,
    matchingTransition: Transition
  ): void {
    const mapsToType = this.getMapsToType(targetNode.nodeDetailsType);
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const sourcePTDVIds = matchingTransition.sourcePTDVIds;
    const schemaMapperKey = this.getSchemaMapperConfigKey(sourcePTDVIds, targetPTDVId, mapsToType);

    const existingSchemaMapperConfig = this.existingSchemaMapperConfigs.get(schemaMapperKey);
    assert(
      existingSchemaMapperConfig != null,
      'Expected to find existing schema mapper config for transition',
      {
        sourcePTDVIds,
        targetPTDVId,
        mapsToType,
      }
    );

    this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
      toNodeConfigClientId: targetNode.id,
      schemaMapperConfigClientId: existingSchemaMapperConfig.clientId,
      schemaMapperDependencies: sourceNodes.map((node) => ({fromNodeConfigClientId: node.id})),
    });
  }

  private getTransitionSourceForNode(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): TransitionSource {
    const taskSchemaNodes = this.nodes.filter((node) => isStaticTaskSchemaNodeData(node));

    const transitionSource =
      getTransitionSourceForNodeIdPair(sourceNode.id, targetNode.id, this.transitions, this.nodes) ??
      getDefaultTransitionSource(sourceNode.id, taskSchemaNodes);
    return transitionSource;
  }

  /**
   * If the field mapper config is a duplicate of a previously generated field mapper config, returns the client ID of that field mapper config.
   * Otherwise, returns the new field mapper config.
   */
  buildFieldMapperConfigFromDataFlowEdge(
    clientId: string,
    dataFlowEdge: FlowchartStaticEdgeData,
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaFieldMapperConfigSpec {
    const sourceNode = this.nodes.find((node) => node.id === dataFlowEdge.source);
    assert(sourceNode != null, 'Expected to find source node', {edge: dataFlowEdge});
    assert(
      isStaticTaskSchemaNodeData(sourceNode),
      'Only task schema type nodes are expected to be mapped from'
    );
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;

    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;

    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(fromFieldHandle);

    const toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(toFieldHandle).fieldClientId;
    const customMappingData = getCustomMappingDataForDataFlowEdge(dataFlowEdge, this.transitions, this.nodes);

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType = this.getMapperTokenTypeFromTransitionSource(transitionSource);

    const fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec = {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(
              fromFieldClientInfo.sectionType,
              sourceNode,
              targetNode,
              fromFieldClientInfo.fieldClientId
            ),
    };

    const config = {
      fieldMapperConfig,
      fromTaskDefinitionVersionId,
      fromNodeConfigClientId: sourceNode.id,
    };

    return config.fieldMapperConfig;
  }

  private getLookupConfigFromFieldAndTransitionSource(
    sectionType: TaskSchemaNodeSectionType,
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData,
    fromFieldClientId: string
  ): LookupConfigSpec {
    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    return this.buildDefaultLookupConfig(
      sectionType,
      transitionSource,
      fromFieldClientId,
      sourceNode.taskDefinitionVersionSpec.clientId
    );
  }

  private buildDefaultLookupConfig(
    sectionType: TaskSchemaNodeSectionType,
    transitionSource: TransitionSource,
    fromFieldClientId: string,
    sourcePTDVId: string
  ): LookupConfigSpec {
    const lookupSteps: Array<LookupStepSpec> = [];
    if (sectionType === TaskSchemaNodeSectionType.INPUTS && transitionSource === TransitionSource.OUTPUTS) {
      // If the transition source is the node output but the field is coming from the node input, we need to add
      // a lookup step to go from the output to the inputs (aka from the process output to the process task).
      lookupSteps.push({type: LookupStepType.PROCESS_TASK});
    } else if (sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      // If the field is in the map from start section we first do a flowchart task lookup
      assert(this.rootNode != null, 'Start node must be present to map from root');
      lookupSteps.push({
        type: LookupStepType.FLOWCHART_PROCESS_TASK,
        processTaskSchema: this.rootNode.taskSchemaId,
      });
      lookupSteps.push({
        type: LookupStepType.SCHEMA_FIELD,
        schemaField: fromFieldClientId,
      });
      return {
        isMulti: false,
        lookupSteps,
      };
    }
    lookupSteps.push({
      type: LookupStepType.PROCESS_TASK_DEFINITION_FIELD,
      processTaskDefinitionFieldIdentifier: fromFieldClientId,
      processTaskDefinitionVersion: sourcePTDVId,
    });
    return {
      isMulti: false,
      lookupSteps,
    };
  }

  /**
   * Fallback for when transitionSourcesBySourcePTDVId has no entry for a source PTDV
   * (e.g. newly created optional transitions). Looks up the node and checks whether it
   * actually has an outputs section — without this, we'd always default to OUTPUTS and
   * the backend would fail with an assertion error for tasks that have no output schema.
   */
  private getDefaultTransitionSourceForPTDVId(sourcePTDVId: string): TransitionSource {
    const sourceNode = this.nodes.find(
      (n) => isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === sourcePTDVId
    );
    if (sourceNode == null) {
      return TransitionSource.OUTPUTS;
    }
    const taskSchemaNodes = this.nodes.filter((node) => isStaticTaskSchemaNodeData(node));
    return getDefaultTransitionSource(sourceNode.id, taskSchemaNodes);
  }

  private getMapperTokenTypeFromTransitionSource(transitionSource: TransitionSource): ProcessMapperTokenType {
    return transitionSource === TransitionSource.OUTPUTS
      ? ProcessMapperTokenType.PROCESS_OUTPUT
      : ProcessMapperTokenType.PROCESS_TASK;
  }
}

/**
 * Builds the default field mapper config for a data flow edge.
 *
 * This is used to differentiate between a "field-to-field" mapping configured by drawing an edge between two
 * fields and a "custom mapping" configured by a custom lookup config.
 */
export function buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
  clientId,
  dataFlowEdge,
  nodes,
  transitions,
}: {
  clientId: string;
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}): ProcessFlowchartSchemaFieldMapperConfigSpec {
  const targetNode = nodes.find((node) => node.id === dataFlowEdge.target);
  assert(targetNode != null && isStaticTaskSchemaNodeData(targetNode), 'Expected to find target node', {
    edge: dataFlowEdge,
  });

  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner({
    nodes,
    edges: [dataFlowEdge],
    transitions,
  });
  return runner.buildFieldMapperConfigFromDataFlowEdge(clientId, dataFlowEdge, targetNode);
}

export default function buildSchemaMapperConfigsFromStaticData(
  args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs
): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner(args);
  return runner.run();
}
